import "./AddToCartInput.scss";
import "@mshop-ui/core-web-components/dist/ui-button";
import "@mshop-ui/core-web-components/dist/ui-input";

import React, { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { Plus, Minus } from "@metro-ui/metro-icons-react";

import WebComponentAdapter from "../../../app/components/WebComponentAdapter";
import { isAllowingMultiOQ } from "../../utils/featureFlagUtil";
const UiButton = WebComponentAdapter("ui-button");
const UiInput = WebComponentAdapter("ui-input");

export enum EmptyValueStrategy {
	IGNORE = "ignore",
	ZERO = "zero",
}

export type AddToCartInputProps = {
	articleId?: string;
	initialValue?: number;
	isDecimalInput: boolean;
	id?: string;
	isDisabled?: boolean;
	hasStepperInput?: boolean;
	multiplier?: number;
	emptyValueStrategy?: EmptyValueStrategy;
	ariaLabel?: string;
	onChangeQuantity: (quantity: number, action: "input" | "plus" | "minus") => void;
	onFocus?: (e: React.FocusEvent) => void;
	onBlur?: (e: React.FocusEvent) => void;
	onHitEnter?: () => void;
};

export type AddToCartInputRef = {
	focus: () => void;
	reset: () => void;
};

export const AddToCartInput = forwardRef(function AddToCartInputComponent(
	{
		initialValue,
		isDecimalInput,
		id = "test-add-to-cart-input",
		isDisabled = false,
		hasStepperInput = false,
		emptyValueStrategy = EmptyValueStrategy.ZERO,
		multiplier = 1,
		onChangeQuantity,
		onFocus = () => {},
		onBlur = () => {},
		onHitEnter = () => {},
	}: Readonly<AddToCartInputProps>,
	ref: React.Ref<AddToCartInputRef>,
) {
	multiplier = isAllowingMultiOQ() ? multiplier : 1;
	const intl = useIntl();
	const ariaLabel = intl.formatMessage({ id: "ORC_QUICKENTRY_AMOUNT_INPUT_PLACEHOLDER" });
	const inputRef = useRef<HTMLInputElement>(null);
	const minusButtonRef = useRef<HTMLButtonElement>(null);
	const plusButtonRef = useRef<HTMLButtonElement>(null);
	const [inputValue, setInputValue] = useState<string>(String(initialValue || ""));

	useEffect(() => {
		setInputValue(initialValue ? String(initialValue) : "");
	}, [initialValue]);

	useImperativeHandle(
		ref,
		() => ({
			focus: () => inputRef.current?.focus(),
			reset: () => {
				setInputValue(String(initialValue || ""));
				onChangeQuantity(parseValue(String(initialValue || "")), "input");
			},
		}),
		[initialValue],
	);

	const parseValue = useCallback(
		(value: string): number => {
			const parsableValue = value.replaceAll(/,/g, ".");
			const parsedValue = isDecimalInput ? parseFloat(parsableValue) : parseInt(parsableValue, 10);
			return isNaN(parsedValue) ? 0 : parsedValue;
		},
		[isDecimalInput],
	);

	const onChange = useCallback(
		(event: ChangeEvent) => {
			const cleanValue = (event.target as HTMLInputElement).value.replaceAll(
				isDecimalInput ? /[^0-9,.]/g : /[^\d]/g,
				"",
			);
			setInputValue(cleanValue);
			if (cleanValue === "" && emptyValueStrategy === EmptyValueStrategy.IGNORE) {
				return;
			}
			onChangeQuantity(parseValue(cleanValue), "input");
		},
		[onChangeQuantity, parseValue],
	);

	const onStep = useCallback(
		(step: number) => {
			const currentValue = parseValue(inputValue);
			const roundFunction = (n: number) => {
				n = Math.round(n * 1000) / 1000;
				return step > 0 ? Math.floor(n) : Math.ceil(n);
			};
			const newValue =
				multiplier !== 1
					? Math.max(
							Math.round(
								roundFunction((currentValue + step * multiplier) / multiplier) * multiplier * 1000,
							) / 1000,
							0,
						)
					: Math.max(parseValue(inputValue) + step, 0);
			setInputValue(String(newValue));
			onChangeQuantity(newValue, step > 0 ? "plus" : "minus");
		},
		[inputValue, onChangeQuantity, parseValue],
	);

	const onClickMinus = useCallback(() => {
		onStep(-1);
	}, [onStep]);

	const onClickPlus = useCallback(() => {
		onStep(1);
	}, [onStep]);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<Element>) => {
			if (event.key === "Enter") {
				event.preventDefault();
				onHitEnter();
			}
		},
		[onHitEnter],
	);

	return (
		<div className={`add-to-cart-input${hasStepperInput ? " wide" : ""}`}>
			{hasStepperInput && (
				<UiButton>
					<button
						ref={minusButtonRef}
						className="add-to-cart-stepper no-print"
						onClick={onClickMinus}
						disabled={isDisabled}
						aria-label="Decrease amount"
					>
						<Minus />
					</button>
				</UiButton>
			)}
			{isDecimalInput ? (
				<UiInput>
					<input
						ref={inputRef}
						name="add-to-cart-input"
						id={id}
						data-testid="test-add-to-cart-input"
						disabled={isDisabled}
						value={inputValue}
						type="text"
						inputMode="decimal"
						pattern="[0-9]+([.,][0-9]{3})?"
						step={0.001}
						onChange={onChange}
						onFocus={onFocus}
						onBlur={onBlur}
						onKeyDown={onKeyDown}
						aria-label={ariaLabel}
					/>
					<span slot="suffix" data-suffix>
						<FormattedMessage id="ORC_KG" />
					</span>
				</UiInput>
			) : (
				<UiInput>
					<input
						ref={inputRef}
						name="add-to-cart-input"
						id={id}
						data-testid="test-add-to-cart-input"
						disabled={isDisabled}
						value={inputValue}
						type="text"
						inputMode="numeric"
						pattern="[0-9]+"
						step={1}
						onChange={onChange}
						onFocus={onFocus}
						onBlur={onBlur}
						onKeyDown={onKeyDown}
						aria-label={ariaLabel}
					/>
				</UiInput>
			)}
			{hasStepperInput && (
				<UiButton>
					<button
						ref={plusButtonRef}
						className="add-to-cart-stepper no-print"
						onClick={onClickPlus}
						disabled={isDisabled}
						aria-label="Increase amount"
					>
						<Plus />
					</button>
				</UiButton>
			)}
		</div>
	);
});
